// src/pages/AdminPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  API_BASE, login, me, listPostsAdmin, createPost,
  updatePost, publishPost, unpublishPost, deletePost
} from '../api/client.js';
import { AlertCircle, Plus, LogOut, RefreshCw, Trash2, CheckCircle2, XCircle } from 'lucide-react';

// simples helpers
const CATS = ['technology','cloud','consulting','development','security','automation'];
const LANGS = ['pt','en','es'];

export function AdminPage() {
  const [authChecking, setAuthChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [err, setErr] = useState('');

  // filtros/listagem
  const [status, setStatus] = useState('draft'); // default
  const [category, setCategory] = useState('');
  const [featured, setFeatured] = useState('');
  const [language, setLanguage] = useState('pt');
  const [page, setPage] = useState(1);
  const [size] = useState(10);

  const [listLoading, setListLoading] = useState(false);
  const [resp, setResp] = useState({ posts: [], total: 0, total_pages: 1 });

  // criação/edição
  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'technology',
    tags: '',
    image_url: '',
    meta_description: '',
    read_time_minutes: 5,
    language: 'pt',
    featured: false,
  });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // ====== auth boot ======
  useEffect(() => {
    (async () => {
      try {
        setAuthChecking(true);
        setErr('');
        const u = await me();
        setUser(u);
      } catch (e) {
        // sem token ou inválido -> fica na tela de login
        setUser(null);
      } finally {
        setAuthChecking(false);
      }
    })();
  }, []);

  // ====== load list ======
  const q = useMemo(() => ({ page, size, status, category: category || undefined,
    featured: featured === '' ? undefined : featured === 'true',
    language }), [page, size, status, category, featured, language]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setListLoading(true);
        setErr('');
        const r = await listPostsAdmin(q);
        setResp(r);
      } catch (e) {
        console.error(e);
        setErr('Falha ao listar posts.');
      } finally {
        setListLoading(false);
      }
    })();
  }, [user, q]);

  const onLogin = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const username = fd.get('username');
    const password = fd.get('password');
    try {
      setErr('');
      await login(username, password);
      const u = await me();
      setUser(u);
    } catch (e) {
      console.error(e);
      setErr('Credenciais inválidas.');
    }
  };

  const logout = () => {
    localStorage.removeItem('hat_token');
    setUser(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'technology',
      tags: '',
      image_url: '',
      meta_description: '',
      read_time_minutes: 5,
      language: 'pt',
      featured: false,
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      // transforma tags string -> array
      tags: (form.tags || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    };
    try {
      setSaving(true);
      setErr('');
      if (!editingId) {
        await createPost(payload);
      } else {
        await updatePost(editingId, payload);
      }
      resetForm();
      // refresh lista
      const r = await listPostsAdmin(q);
      setResp(r);
    } catch (e) {
      console.error(e);
      setErr('Erro ao salvar post. Verifique campos e slug único.');
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async (id) => {
    try {
      await publishPost(id);
      const r = await listPostsAdmin(q);
      setResp(r);
    } catch (e) {
      console.error(e);
      setErr('Erro ao publicar post.');
    }
  };

  const onUnpublish = async (id) => {
    try {
      await unpublishPost(id);
      const r = await listPostsAdmin(q);
      setResp(r);
    } catch (e) {
      console.error(e);
      setErr('Erro ao despublicar post.');
    }
  };

  const onDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este post?')) return;
    try {
      await deletePost(id);
      const r = await listPostsAdmin(q);
      setResp(r);
    } catch (e) {
      console.error(e);
      setErr('Erro ao excluir post.');
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt,
      content: p.content,
      category: p.category,
      tags: (p.tags || []).join(', '),
      image_url: p.image_url || '',
      meta_description: p.meta_description || '',
      read_time_minutes: p.read_time_minutes || 5,
      language: p.language || 'pt',
      featured: !!p.featured,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (authChecking) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-gray-500">Verificando sessão…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <form onSubmit={onLogin} className="bg-white shadow-xl rounded-xl p-8 w-full max-w-sm space-y-4">
          <h1 className="text-2xl font-bold">Admin Login</h1>
          <div>
            <label className="block text-sm mb-1">Usuário</label>
            <input name="username" className="w-full border rounded px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm mb-1">Senha</label>
            <input name="password" type="password" className="w-full border rounded px-3 py-2" required />
          </div>
          {err && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {err}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-black text-white rounded-lg py-2 hover:opacity-90"
          >
            Entrar
          </button>
          <p className="text-xs text-gray-400">API: {API_BASE}</p>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin • Blog</h1>
          <p className="text-sm text-gray-500">Logado como <b>{user.username}</b></p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
            title="Recarregar"
          >
            <RefreshCw className="w-4 h-4" /> Recarregar
          </button>
          <button
            onClick={logout}
            className="px-3 py-2 rounded-lg bg-gray-900 text-white hover:opacity-90 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </div>

      {/* Form Create/Update */}
      <form onSubmit={onSave} className="bg-white rounded-xl shadow p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {editingId ? 'Editar Post' : 'Novo Post'}
          </h2>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-sm text-gray-600 hover:underline">
              cancelar edição
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Título">
            <input className="w-full border rounded px-3 py-2"
              value={form.title} onChange={(e)=>setForm(f=>({...f,title:e.target.value}))} required minLength={5} />
          </Field>
          <Field label="Slug (a-z0-9-)">
            <input className="w-full border rounded px-3 py-2"
              value={form.slug} onChange={(e)=>setForm(f=>({...f,slug:e.target.value}))} required minLength={5} />
          </Field>
          <Field label="Resumo (excerpt)">
            <input className="w-full border rounded px-3 py-2"
              value={form.excerpt} onChange={(e)=>setForm(f=>({...f,excerpt:e.target.value}))} required minLength={20} />
          </Field>
          <Field label="Imagem (URL)">
            <input className="w-full border rounded px-3 py-2"
              value={form.image_url} onChange={(e)=>setForm(f=>({...f,image_url:e.target.value}))} placeholder="https://..." />
          </Field>
          <Field label="Categoria">
            <select className="w-full border rounded px-3 py-2"
              value={form.category} onChange={(e)=>setForm(f=>({...f,category:e.target.value}))}>
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Idioma">
            <select className="w-full border rounded px-3 py-2"
              value={form.language} onChange={(e)=>setForm(f=>({...f,language:e.target.value}))}>
              {LANGS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Meta description (≤160)">
            <input className="w-full border rounded px-3 py-2"
              value={form.meta_description} onChange={(e)=>setForm(f=>({...f,meta_description:e.target.value}))} maxLength={160} />
          </Field>
          <Field label="Tempo de leitura (min)">
            <input type="number" min={1} max={60} className="w-full border rounded px-3 py-2"
              value={form.read_time_minutes} onChange={(e)=>setForm(f=>({...f,read_time_minutes:parseInt(e.target.value||'5',10)}))} />
          </Field>
          <Field label="Tags (separadas por vírgula)">
            <input className="w-full border rounded px-3 py-2"
              value={form.tags} onChange={(e)=>setForm(f=>({...f,tags:e.target.value}))} />
          </Field>
          <Field label="Destaque?">
            <select className="w-full border rounded px-3 py-2"
              value={form.featured ? 'true':'false'} onChange={(e)=>setForm(f=>({...f,featured:e.target.value==='true'}))}>
              <option value="false">Não</option>
              <option value="true">Sim</option>
            </select>
          </Field>
        </div>

        <Field label="Conteúdo (mín. 100 chars)">
          <textarea className="w-full border rounded px-3 py-2 min-h-[160px]"
            value={form.content} onChange={(e)=>setForm(f=>({...f,content:e.target.value}))} required />
        </Field>

        {err && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {err}
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-black text-white rounded-lg px-4 py-2 hover:opacity-90 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> {editingId ? 'Salvar alterações' : 'Criar post'}
        </button>
      </form>

      {/* Filtros + Lista */}
      <div className="bg-white rounded-xl shadow p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Status" value={status} onChange={setStatus}
                  options={['draft','published','archived']} withEmpty={false}/>
          <Select label="Categoria" value={category} onChange={setCategory}
                  options={CATS} withEmpty />
          <Select label="Featured" value={featured} onChange={setFeatured}
                  options={['true','false']} withEmpty />
          <Select label="Idioma" value={language} onChange={setLanguage}
                  options={LANGS} withEmpty={false}/>
          <button
            onClick={() => setPage(1)}
            className="border rounded-lg px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
            title="Aplicar filtros"
          >
            <RefreshCw className="w-4 h-4" /> Aplicar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-3">Título</th>
                <th className="py-2 pr-3">Slug</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Idioma</th>
                <th className="py-2 pr-3">Categoria</th>
                <th className="py-2 pr-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {listLoading && (
                <tr><td colSpan={6} className="py-6 text-gray-500">Carregando…</td></tr>
              )}
              {!listLoading && resp.posts?.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-gray-500">Sem resultados</td></tr>
              )}
              {!listLoading && resp.posts?.map((p) => (
                <tr key={p.id} className="border-b last:border-b-0">
                  <td className="py-2 pr-3">{p.title}</td>
                  <td className="py-2 pr-3 text-gray-600">{p.slug}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-1 rounded text-xs ${
                      p.status === 'published' ? 'bg-green-100 text-green-700'
                      : p.status === 'draft' ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                    }`}>{p.status}</span>
                  </td>
                  <td className="py-2 pr-3">{p.language}</td>
                  <td className="py-2 pr-3">{p.category}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      {p.status !== 'published' ? (
                        <button onClick={()=>onPublish(p.id)} className="text-green-700 hover:underline flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> publicar
                        </button>
                      ) : (
                        <button onClick={()=>onUnpublish(p.id)} className="text-amber-700 hover:underline flex items-center gap-1">
                          <XCircle className="w-4 h-4" /> despublicar
                        </button>
                      )}
                      <button onClick={()=>startEdit(p)} className="text-blue-700 hover:underline">editar</button>
                      <button onClick={()=>onDelete(p.id)} className="text-red-700 hover:underline flex items-center gap-1">
                        <Trash2 className="w-4 h-4" /> excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 justify-end">
          <span className="text-sm text-gray-500">
            Página {page} de {resp.total_pages || 1} — {resp.total} itens
          </span>
          <button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}
                  className="px-3 py-1 border rounded disabled:opacity-50">Anterior</button>
          <button disabled={page>=(resp.total_pages||1)} onClick={()=>setPage(p=>p+1)}
                  className="px-3 py-1 border rounded disabled:opacity-50">Próxima</button>
        </div>
      </div>
    </div>
  );
}

// componentes pequenos para evitar repetir markup
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm mb-1">{label}</span>
      {children}
    </label>
  );
}

function Select({ label, value, onChange, options, withEmpty = false }) {
  return (
    <label className="block">
      <span className="block text-sm mb-1">{label}</span>
      <select className="border rounded px-3 py-2"
        value={value}
        onChange={(e)=>onChange(e.target.value)}
      >
        {withEmpty && <option value="">(todos)</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
